import { useMutation } from '@apollo/client';
import { RuleVariantUnion } from '@procureai/rule-builder/';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useCallback } from 'react';
import { v7 as uuidv7 } from 'uuid';

import { GET_RULE_DATA } from '../useRuleDialogActions/Rule.query.gql';
import { TsrctRuleVariant } from '@/generated/gql/graphql';
import { RULE_LIST_QUERY } from '~/routes/RuleListSourcing/RuleListSourcingQuery.gql';

import { CREATE_RULE_SETTINGS, REMOVE_RULE_SETTINGS, UPDATE_RULE_SETTINGS } from './Rule.mutation.gql';

export interface RuleCreateInput {
  name: string;
  responsibleUsers: string[];
  variant: RuleVariantUnion;
}

export interface RuleUpdateInput {
  id: string;
  name: string;
  responsibleUsers: string[];
}

export interface RuleRemoveInput {
  id: string;
}

export const supportedRuleVariants = [
  TsrctRuleVariant.SOURCING,
  TsrctRuleVariant.COMMODITY_COVERAGE_REQUEST,
] as const satisfies RuleVariantUnion[];

export const ruleVariantToTesseractVariantMap: Record<RuleVariantUnion, TsrctRuleVariant | undefined> = {
  SOURCING: TsrctRuleVariant.SOURCING,
  COMMODITY_COVERAGE_REQUEST: TsrctRuleVariant.COMMODITY_COVERAGE_REQUEST,
  COMPLIANCE_CHECK: undefined,
  DEMAND_PROJECT: undefined,
  DEMAND_PROJECT_INTAKE: undefined,
  SUPPLIER_LIFECYCLE: undefined,
};

export const toTesseractRuleVariant = (variant: RuleVariantUnion): TsrctRuleVariant => {
  const tesseractVariant = ruleVariantToTesseractVariantMap[variant];

  if (!tesseractVariant) {
    throw new Error(`Rule variant "${variant}" is not supported.`);
  }

  return tesseractVariant;
};

export const useRuleActions = () => {
  const { onError } = useCustomSnackbar();

  const [createMutation, { loading: createLoading }] = useMutation(CREATE_RULE_SETTINGS, { onError });
  const [updateMutation, { loading: updateLoading }] = useMutation(UPDATE_RULE_SETTINGS, {
    refetchQueries: [GET_RULE_DATA],
    onError,
  });
  const [deleteMutation, { loading: deleteLoading }] = useMutation(REMOVE_RULE_SETTINGS, {
    refetchQueries: [
      {
        query: RULE_LIST_QUERY,
        fetchPolicy: 'network-only',
      },
    ],
    onError,
  });

  const create = useCallback(
    async ({ variant, ...input }: RuleCreateInput) => {
      const newId = uuidv7();
      const { data } = await createMutation({
        variables: { id: newId, data: { ...input, variant: toTesseractRuleVariant(variant) } },
      });
      return data?.tesseract.rule.create.id;
    },
    [createMutation],
  );

  const update = useCallback(
    async ({ id, ...data }: RuleUpdateInput) => {
      await updateMutation({ variables: { id, data } });
    },
    [updateMutation],
  );

  const remove = useCallback(
    async (variables: RuleRemoveInput) => {
      await deleteMutation({ variables });
    },
    [deleteMutation],
  );

  return {
    loading: createLoading || updateLoading || deleteLoading,
    create,
    update,
    remove,
  };
};
